import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { Navigate, useSearchParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState(params.get("error") || "");

  // Clear the query param on mount so refresh doesn't re-show the banner,
  // but preserve the message in local state so the user still sees it.
  React.useEffect(() => {
    if (params.get("error")) {
      setError(params.get("error"));
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-screen" />;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      <div className="lg:col-span-3 bg-zinc-950 text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div>
          <div className="label-overline text-white/60">VizePanel — Vize Danışmanlığı Yönetimi</div>
          <h1 className="mt-8 font-display font-black text-5xl lg:text-7xl leading-none tracking-tight">
            Randevu, ödeme<br />ve <span className="text-[#4d7cff]">tedarikçi cari</span><br />tek panelde.
          </h1>
          <p className="mt-8 max-w-lg text-white/70 leading-relaxed">
            Müşteri, ülke, seyahat tarihi, parçalı EUR/TL tahsilat ve tedarikçiye borç/ödeme akışını
            excel karmaşasından çıkarın. Pasaport fotoğrafı yükleyin; alanlar otomatik dolsun.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-3 gap-6 max-w-2xl">
          {[
            ["01", "Pasaport OCR"],
            ["02", "Parçalı Ödeme"],
            ["03", "Tedarikçi Cari"],
          ].map(([n, t]) => (
            <div key={n} className="border-t border-white/20 pt-4">
              <div className="text-white/40 text-xs tracking-widest">{n}</div>
              <div className="mt-2 font-display font-bold text-lg">{t}</div>
            </div>
          ))}
        </div>
        <div className="absolute -right-40 -bottom-40 w-[500px] h-[500px] rounded-full bg-[#0033cc]/30 blur-3xl" />
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-zinc-50">
        <div className="w-full max-w-sm">
          <div className="label-overline">Giriş</div>
          <h2 className="mt-3 font-display font-black text-3xl tracking-tight">Panele hoş geldiniz</h2>
          <p className="mt-3 text-sm text-zinc-500">Google hesabınızla giriş yaparak devam edin.</p>
          {error && (
            <div className="mt-6 flex items-start gap-3 p-3 border border-red-200 bg-red-50 rounded-md" data-testid="login-error-banner">
              <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800 leading-relaxed">{error}</div>
            </div>
          )}
          <button
            data-testid="login-google-btn"
            onClick={handleLogin}
            className="mt-8 w-full h-12 bg-zinc-950 text-white font-medium rounded-md hover:bg-zinc-800 transition-colors flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.5 34.6 26.9 36 24 36c-5.3 0-9.7-3.1-11.3-7.4l-6.5 5C9.5 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.4l6.5 5.5C42 34.5 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            Google ile Giriş Yap
          </button>
          <div className="mt-8 text-xs text-zinc-400 leading-relaxed">
            Yalnızca yetkili sahip hesabı erişebilir. Oturum 7 gün geçerlidir.
          </div>
        </div>
      </div>
    </div>
  );
}
